import { useField, FieldHookConfig, useFormikContext } from 'formik';
import { useState, useEffect } from 'react';
import cx from 'classnames';

import styles from './TextInput.module.scss';

interface Props extends React.HTMLProps<HTMLInputElement> {
  fullBorder?: boolean;
  label?: string;
  className?: string;
  required?: boolean;
  validationAttemptCount?: number;
}

const TextInput = ({
  label,
  fullBorder,
  className,
  required,
  validationAttemptCount,
  ...props
}: Props & FieldHookConfig<string>): JSX.Element => {
  const [field, meta] = useField(props);
  const { submitCount } = useFormikContext();
  const [lastSubmitCount, setLastSubmitCount] = useState(submitCount);
  const [lastValidationAttempt, setLastValidationAttempt] = useState(validationAttemptCount || 0);
  const [shouldShake, setShouldShake] = useState(false);

  const hasError = meta.touched && meta.error;

  // Trigger shake when validation is attempted with error
  useEffect(() => {
    const submitCountIncreased = submitCount > lastSubmitCount;
    const validationAttemptIncreased = validationAttemptCount && validationAttemptCount > lastValidationAttempt;
    const hasErrorNow = meta.touched && meta.error;

    if ((submitCountIncreased || validationAttemptIncreased) && hasErrorNow) {
      setShouldShake(true);
      setLastSubmitCount(submitCount);
      setLastValidationAttempt(validationAttemptCount || 0);

      // Remove shake class after animation completes (500ms)
      const timer = setTimeout(() => {
        setShouldShake(false);
      }, 500);

      return () => clearTimeout(timer);
    } else if (submitCountIncreased) {
      setLastSubmitCount(submitCount);
    } else if (validationAttemptIncreased) {
      setLastValidationAttempt(validationAttemptCount || 0);
    }
  }, [submitCount, validationAttemptCount, meta.error, meta.touched, lastSubmitCount, lastValidationAttempt]);

  return (
    <div className={styles.container}>
      {label && (
        <label
          className={cx(styles.label, {
            [`${styles.labelShake}`]: shouldShake,
          })}
          htmlFor={props.name}
        >
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      <input
        {...field}
        {...props}
        className={cx(styles.input, className, {
          [`${styles.fullBorder}`]: fullBorder,
          [`${styles.error}`]: shouldShake,
          [`${styles.hasError}`]: hasError && !shouldShake,
        })}
      />
      <div className={styles.errorHint}>{hasError ? meta.error : null}</div>
    </div>
  );
};

export default TextInput;
